import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import type { User } from '@/@types/global';

import type { GetPatientLatestImagesResponseV2 } from '../@types/latestImagesV2';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

interface UpdateFmxSortParams {
  imagingPatientId: string;
  estimatedVisitId: number;
  fmxSortKeyToApply: string;
}

export const useUpdateFmxSort = () => {
  const queryClient = useQueryClient();
  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );
  const primaryPracticeId = user?.primaryPracticeId;
  const primaryOrganizationId = user?.primaryOrganizationId;

  return useMutation<
    GetPatientLatestImagesResponseV2,
    AxiosError,
    UpdateFmxSortParams
  >({
    mutationFn: async ({
      imagingPatientId,
      estimatedVisitId,
      fmxSortKeyToApply,
    }) => {
      return makeAPIRequestReactQuery<GetPatientLatestImagesResponseV2>(
        `/${endpoints.patient}/${imagingPatientId}/${endpoints.updateFmxSort}`,
        {
          estimatedVisitId,
          fmxSortKeyToApply,
        },
        'POST',
        {
          headers: {
            Org: primaryOrganizationId,
            Practice: primaryPracticeId,
          },
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [endpoints.patient, endpoints.latestImages],
        refetchType: 'all',
      });
    },
  });
};
